import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, MailCheck } from "lucide-react";
import { errorMessage, post } from "@/lib/api";
import { FieldError, usePageTitle } from "@/components/States";
import { Brand } from "@/components/layout/SidebarShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function ForgotPassword() {
  usePageTitle("Forgot password");
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!EMAIL_RE.test(email.trim())) {
      setError("Enter a valid email address");
      return;
    }
    setError("");
    setBusy(true);
    try {
      await post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-900 p-6">
      {sent ? (
        <div className="w-full max-w-md space-y-5 rounded-xl border border-slate-200 bg-white p-8 shadow-xl" data-testid="forgot-password-sent">
          <div className="inline-block rounded-lg bg-slate-900 p-3"><Brand roleLabel="For warrant teams" /></div>
          <MailCheck className="h-10 w-10 text-blue-700" />
          <h1 className="font-display text-xl font-bold text-slate-900">Check your email</h1>
          <p className="text-sm text-slate-600">If there's an account for <strong>{email.trim()}</strong>, we've sent a link to reset your password. It expires in 1 hour.</p>
          <Link to="/login" className="inline-block text-sm font-semibold text-blue-700 hover:underline" data-testid="forgot-password-back-link">Back to sign in</Link>
        </div>
      ) : (
        <form onSubmit={submit} noValidate className="w-full max-w-md space-y-5 rounded-xl border border-slate-200 bg-white p-8 shadow-xl" data-testid="forgot-password-form">
          <div className="inline-block rounded-lg bg-slate-900 p-3"><Brand roleLabel="For warrant teams" /></div>
          <div>
            <h1 className="font-display text-2xl font-extrabold text-slate-900">Forgot your password?</h1>
            <p className="mt-1 text-sm text-slate-500">Enter your email and we'll send you a link to choose a new one.</p>
          </div>
          <div>
            <Label htmlFor="fp-email">Email</Label>
            <Input id="fp-email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-11" data-testid="forgot-password-email-input" />
            <FieldError message={error} testId="forgot-password-error" />
          </div>
          <Button type="submit" disabled={busy} className="h-11 w-full bg-blue-700 hover:bg-blue-800" data-testid="forgot-password-submit-button">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Send reset link
          </Button>
          <p className="text-center text-sm"><Link to="/login" className="font-semibold text-blue-700 hover:underline" data-testid="forgot-password-login-link">Back to sign in</Link></p>
        </form>
      )}
    </div>
  );
}
